import { useCallback, useMemo, useState } from 'react';
import type { AxisConfiguration } from 'components/molecules/PackArchitecture/AxisConfigurationModal';
import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/KpiAxisConfigurationModal';

type UseWhitespaceAxesControlsParams = {
    xAxis: string;
    yAxis: string;
    crossSection: string;
    setXAxis: (value: string) => void;
    setYAxis: (value: string) => void;
    setCrossSection: (value: string) => void;
    applyXAxisDimensionConfig: (config: AxisConfiguration) => void;
    applyYAxisKpiConfig: (config: KpiAxisConfiguration) => void;
    applyYAxisDimensionConfig: (config: AxisConfiguration) => void;
    markConfigRefreshStart: () => void;
    trackAxesMenuOpened: () => void;
    trackXAxisSelected: (previousXAxis: string, selectedXAxis: string) => void;
    trackYAxisSelected: (previousYAxis: string, selectedYAxis: string) => void;
    trackCrossSectionOpened: () => void;
    trackCrossSectionSelected: (previousCrossSection: string, selectedCrossSection: string) => void;
};

const useWhitespaceAxesControls = ({
    xAxis,
    yAxis,
    crossSection,
    setXAxis,
    setYAxis,
    setCrossSection,
    applyXAxisDimensionConfig,
    applyYAxisKpiConfig,
    applyYAxisDimensionConfig,
    markConfigRefreshStart,
    trackAxesMenuOpened,
    trackXAxisSelected,
    trackYAxisSelected,
    trackCrossSectionOpened,
    trackCrossSectionSelected,
}: UseWhitespaceAxesControlsParams) => {
    const [isCrossSectionOpen, setIsCrossSectionOpen] = useState(false);

    const handleAxisMenuOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                trackAxesMenuOpened();
            }
        },
        [trackAxesMenuOpened],
    );

    const handleXAxisChange = useCallback(
        (nextValue: string) => {
            if (nextValue === xAxis) {
                return;
            }
            trackXAxisSelected(xAxis, nextValue);
            markConfigRefreshStart();
            setXAxis(nextValue);
        },
        [markConfigRefreshStart, setXAxis, trackXAxisSelected, xAxis],
    );

    const handleYAxisChange = useCallback(
        (nextValue: string) => {
            if (nextValue === yAxis) {
                return;
            }
            trackYAxisSelected(yAxis, nextValue);
            markConfigRefreshStart();
            setYAxis(nextValue);
        },
        [markConfigRefreshStart, setYAxis, trackYAxisSelected, yAxis],
    );

    const handleXAxisConfigurationChange = useCallback(
        (config: AxisConfiguration) => {
            markConfigRefreshStart();
            applyXAxisDimensionConfig(config);
        },
        [applyXAxisDimensionConfig, markConfigRefreshStart],
    );

    const handleYAxisKpiConfigurationChange = useCallback(
        (config: KpiAxisConfiguration) => {
            markConfigRefreshStart();
            applyYAxisKpiConfig(config);
        },
        [applyYAxisKpiConfig, markConfigRefreshStart],
    );

    const handleYAxisDimensionConfigurationChange = useCallback(
        (config: AxisConfiguration) => {
            markConfigRefreshStart();
            applyYAxisDimensionConfig(config);
        },
        [applyYAxisDimensionConfig, markConfigRefreshStart],
    );

    const handleCrossSectionOpenChange = useCallback(
        (open: boolean) => {
            setIsCrossSectionOpen(open);
            if (open) {
                trackCrossSectionOpened();
            }
        },
        [trackCrossSectionOpened],
    );

    const handleCrossSectionSelect = useCallback(
        (option: string) => {
            trackCrossSectionSelected(crossSection, option);
            markConfigRefreshStart();
            setCrossSection(option);
            setIsCrossSectionOpen(false);
        },
        [crossSection, markConfigRefreshStart, setCrossSection, trackCrossSectionSelected],
    );

    const axesControlState = useMemo(
        () => ({
            isCrossSectionOpen,
            handleAxisMenuOpenChange,
            handleXAxisChange,
            handleYAxisChange,
            handleXAxisConfigurationChange,
            handleYAxisKpiConfigurationChange,
            handleYAxisDimensionConfigurationChange,
            handleCrossSectionOpenChange,
            handleCrossSectionSelect,
        }),
        [
            handleAxisMenuOpenChange,
            handleCrossSectionOpenChange,
            handleCrossSectionSelect,
            handleXAxisChange,
            handleXAxisConfigurationChange,
            handleYAxisChange,
            handleYAxisDimensionConfigurationChange,
            handleYAxisKpiConfigurationChange,
            isCrossSectionOpen,
        ],
    );

    return axesControlState;
};

export default useWhitespaceAxesControls;
